<template>
	<div>
		<h1>Utilisateurs</h1>

		<b-container fluid class="p-0 mb-3">
			<b-row class="align-items-end">
				<b-col cols="auto">
					<div>Rechercher</div>
					<b-input-group style="width: 220px">
						<b-form-input
							v-model="search"
							placeholder="Identifiant, nom..."
							@input="onSearchInput"
						></b-form-input>
						<b-input-group-append>
							<b-button
								variant="outline-secondary"
								v-if="search"
								@click="clearSearch"
							>
								<i class="fas fa-times"></i>
							</b-button>
						</b-input-group-append>
					</b-input-group>
				</b-col>

				<b-col cols="auto">
					<div>Groupe</div>
					<b-form-select
						v-model="selectedGroup"
						@change="applyFilters"
						style="width: 180px">
						<b-form-select-option value=""
							>Tous les groupes</b-form-select-option
						>
						<b-form-select-option value="_none_"
							>Sans groupe</b-form-select-option
						>
						<b-form-select-option
							v-for="g in groups"
							:key="g.uid"
							:value="g.uid"
							>{{ g.name }}</b-form-select-option
						>
					</b-form-select>
				</b-col>

				<b-col cols="auto">
					<div>Statut</div>
					<b-form-select
						v-model="selectedStatus"
						@change="applyFilters"
						style="width: 160px">
						<b-form-select-option value=""
							>Tous</b-form-select-option
						>
						<b-form-select-option value="active"
							>Actif</b-form-select-option
						>
						<b-form-select-option value="inactive"
							>Inactif</b-form-select-option
						>
						<b-form-select-option value="pending"
							>Validation en cours</b-form-select-option
						>
					</b-form-select>
				</b-col>

				<b-col cols="auto">
					<b-button
						variant="outline-secondary"
						@click="resetFilters">
						<i class="fas fa-redo"></i> Réinitialiser
					</b-button>
				</b-col>
			</b-row>
		</b-container>

		<b-table
			striped
			hover
			no-local-sorting
			:items="users"
			:fields="fields"
			:sort-by.sync="sortBy"
			:sort-desc.sync="sortDesc"
			:busy="loading"
			show-empty
			empty-text="Aucun utilisateur trouvé"
			@sort-changed="onSortChanged">
			<template v-slot:cell(uid)="data">
				<router-link
					:to="{ name: 'utilisateur', params: { id: data.value } }"
					><i class="fas fa-edit"></i
				></router-link>
			</template>
			<template #table-busy>
				<div class="text-center my-3">
					<b-spinner></b-spinner>
				</div>
			</template>
		</b-table>

		<b-pagination
			v-if="totalRows > perPage"
			v-model="currentPage"
			:total-rows="totalRows"
			:per-page="perPage"
			align="center"
			class="mt-3"
		></b-pagination>

		<b-button @click="newUser()">Nouveau utilisateur</b-button>
	</div>
</template>

<script>
	import store from "../store";

	import { uuid } from "vue-uuid";

	import usersService from "../services/users.js";
	import client from "../services/client.js";

	export default {
		name: "users",
		data() {
			return {
				sortBy: "identifiant",
				sortDesc: false,
				search: "",
				selectedGroup: "",
				selectedStatus: "",
				currentPage: 1,
				perPage: 20,
				totalRows: 0,
				loading: false,
				searchTimeout: null,
				users: [],
				groups: [],
				obj_groups: {},
				env_names: {},
				status_labels: {
					active: "oui",
					inactive: "non",
					pending: "Validation en cours",
				},
				fields: [
					{
						key: "identifiant",
						label: "Identifiant",
						sortable: true,
					},
					{
						key: "prenom",
						label: "Prénom",
						sortable: true,
					},
					{
						key: "nom",
						label: "Nom",
						sortable: true,
					},
					{
						key: "email",
						label: "Email",
						sortable: true,
					},
					{
						key: "groups",
						label: "Groupes",
						sortable: false,
					},
					{
						key: "env",
						label: "Env",
						sortable: true,
					},
					{
						key: "actif",
						label: "Actif",
						sortable: false,
					},
					{
						key: "last_date_login",
						label: "Dernière connexion",
						sortable: true,
					},
					{
						key: "uid",
						label: "",
					},
				],
			};
		},
		watch: {
			currentPage() {
				this.fetchUsers();
			},
		},
		methods: {
			newUser() {
				this.$router.push({
					name: "utilisateur",
					params: { id: this.$uuid.v4() },
				});
			},
			onSearchInput() {
				clearTimeout(this.searchTimeout);
				this.searchTimeout = setTimeout(() => {
					this.applyFilters();
				}, 500);
			},
			clearSearch() {
				this.search = "";
				this.applyFilters();
			},
			resetFilters() {
				this.search = "";
				this.selectedGroup = "";
				this.selectedStatus = "";
				this.sortBy = "identifiant";
				this.sortDesc = false;
				this.applyFilters();
			},
			onSortChanged(ctx) {
				this.sortBy = ctx.sortBy;
				this.sortDesc = ctx.sortDesc;
				this.applyFilters();
			},
			applyFilters() {
				this.currentPage = 1;
				this.fetchUsers();
			},
			async fetchUsers() {
				this.loading = true;
				try {
					var params = {
						search: this.search,
						sort: this.sortBy,
						dir: this.sortDesc ? "DESC" : "ASC",
						page: this.currentPage,
						limit: this.perPage,
					};
					if (this.selectedGroup)
						params.group_uid = this.selectedGroup;
					if (this.selectedStatus)
						params.status = this.selectedStatus;

					var data = await client.getClientUsers(params);
					var users = data.users || [];

					for (var i = 0; i < users.length; i++) {
						if (users[i].groups) {
							users[i].groups = users[i].groups
								.split("|")
								.map((uid) =>
									this.obj_groups[uid]
										? this.obj_groups[uid].name
										: uid
								)
								.join(", ");
						}
						users[i].actif =
							this.status_labels[users[i].status] || "oui";
						if (this.env_names[users[i].env])
							users[i].env = this.env_names[users[i].env];
					}

					this.users = users;
					this.totalRows = data.pagination
						? data.pagination.total
						: users.length;
				} finally {
					this.loading = false;
				}
			},
		},
		created: async function () {
			if (
				store.state.user.rules &&
				!store.state.user.rules.utilisateurs
			) {
				this.$router.push({ name: "profile" });
			}

			var groups = await client.getClientGroups();
			this.groups = groups;
			for (var i = 0; i < groups.length; i++) {
				this.obj_groups[groups[i].uid] = groups[i];
			}

			let response = await usersService.getSelf(true);
			this.client = response.client;
			var keys = Object.keys(this.client.details.env);
			for (var i = 0; i < keys.length; i++) {
				this.env_names[keys[i]] = this.client.details.env[keys[i]].name;
			}

			await this.fetchUsers();
		},
	};
</script>

<style></style>
